<template>
  <div
    @mouseover="emit('mouseover')"
    @mouseleave="emit('mouseleave')"
    :class="['card', { selected: isSelected }]"
    @click="selectClaim"
  >
    <header>
      <div v-if="companyLogos && companyLogos?.length > 0" class="icon-overlay">
        <component
          v-for="(logo, index) in companyLogos"
          :key="logo + '-' + index"
          :is="logo !== '' ? 'img' : 'div'"
          :src="logo !== '' ? logo : undefined"
          :style="{
            right: `${20 * index}px`,
            zIndex: (companyLogos ? companyLogos?.length : 1) - index,
            width: `${52 - index * 8}px`,
            height: `${52 - index * 8}px`,
            backgroundColor:
              logo === '' ? 'var(--palette-color-default-lighten-95)' : 'var(--palette-color-base-white)',
          }"
          class="logo"
        />
      </div>
      <div v-else class="img-placeholder"></div>
      <div class="selection-container">
        <span>{{ isSelected ? 'Selected' : 'Select' }}</span>
        <div class="radio-box">
          <svg class="icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="i_Check">
              <path
                id="Vector"
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M14.2071 3.79289C14.5976 4.18342 14.5976 4.81658 14.2071 5.20711L7.20711 12.2071C6.81658 12.5976 6.18342 12.5976 5.79289 12.2071L2.29289 8.70711C1.90237 8.31658 1.90237 7.68342 2.29289 7.29289C2.68342 6.90237 3.31658 6.90237 3.70711 7.29289L6.5 10.0858L12.7929 3.79289C13.1834 3.40237 13.8166 3.40237 14.2071 3.79289Z"
                fill="currentColor"
              />
            </g>
          </svg>
        </div>
      </div>
    </header>
    <div class="card-content">
      <span class="name">{{ formatWorkerName(props.claim.worker_name) }}</span>
      <UserRatingDisplay :rating="props.claim.raise_rating_avg" />
      <div class="location">{{ props.claim.raise_shifts_worked || 0 }} shifts with Raise</div>
      <div class="stats">
        <div class="stat-cont">
          <div class="stat">{{ hoursInRoleText }} hours</div>
          <div class="explainer">as {{ props.claim.shift_role }}</div>
        </div>
        <div class="divider"></div>
        <div class="stat-cont">
          <div class="stat">{{ hoursInFunctionText }} hours</div>
          <div class="explainer">as {{ props.claim.shift_function }}</div>
        </div>
      </div>
      <b-button
        v-b-modal="'candidate_profile_' + props.claim.worker_id"
        @click.prevent="selectClaim"
        class="profile-button"
        variant="outline-primary"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M22 12C22 6.486 17.514 2 12 2C6.486 2 2 6.486 2 12C2 14.889 3.239 17.487 5.204 19.315C5.236 19.355 5.279 19.383 5.317 19.418C7.091 21.017 9.43 22 12 22C14.568 22 16.905 21.018 18.678 19.422C18.718 19.386 18.764 19.356 18.797 19.314C20.762 17.486 22 14.888 22 12ZM4 12C4 7.589 7.589 4 12 4C16.411 4 20 7.589 20 12C20 13.93 19.313 15.702 18.171 17.086C17.59 16.318 16.882 15.652 16.055 15.145C15.789 14.982 15.509 14.844 15.226 14.716C16.158 13.848 16.75 12.621 16.75 11.251C16.75 8.632 14.619 6.501 12 6.501C9.381 6.501 7.25 8.632 7.25 11.251C7.25 12.622 7.842 13.849 8.774 14.717C8.491 14.845 8.212 14.983 7.946 15.146C7.118 15.654 6.41 16.319 5.829 17.087C4.687 15.702 4 13.93 4 12ZM12 14C10.483 14 9.25 12.767 9.25 11.25C9.25 9.733 10.483 8.5 12 8.5C13.517 8.5 14.75 9.733 14.75 11.25C14.75 12.767 13.517 14 12 14ZM7.29 18.452C7.74 17.809 8.317 17.262 8.992 16.849C10.803 15.735 13.196 15.735 15.009 16.849C15.682 17.262 16.26 17.809 16.71 18.452C15.386 19.421 13.761 20 12 20C10.238 20 8.613 19.421 7.29 18.452Z"
            fill="#8F5DE7"
          />
        </svg>
        View full profile</b-button
      >
    </div>
    <CandidateProfile :claim="props.claim" />
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue';
import UserRatingDisplay from '@/pages/talent-pool/components/UserRatingDisplay.vue';
import CandidateProfile from '@/pages/talent-pool/shift/components/CandidateProfile.vue';
import {
  getCompanyLogosFromRoles,
  getHoursInRole,
  getHoursInFunction,
  formatWorkerName,
} from '@/pages/talent-pool/utils/profile';

const props = defineProps({
  claim: {
    type: Object,
    required: true,
  },
  selected: Boolean,
  roles: { type: String },
  shift: { type: Object },
});

const emit = defineEmits(['claimSelected', 'mouseover', 'mouseleave']);

const isSelected = computed(() => props.selected);

const hoursInRoleText = computed(() => {
  return getHoursInRole(props.claim.roles);
});

const hoursInFunctionText = computed(() => {
  return getHoursInFunction(props.claim.roles);
});

const companyLogos = computed(() => {
  const logos = getCompanyLogosFromRoles(props.claim?.roles);
  const paddedLogos = [...logos];
  while (paddedLogos.length < 3) {
    paddedLogos.push('');
  }
  return paddedLogos;
});

const selectClaim = () => {
  emit('claimSelected');
};
</script>

<style scoped>
.card {
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  background-color: var(--palette-color-base-white);
  overflow: hidden;
  padding: 1rem;
  border: 1px solid var(--palette-color-default-lighten-90);
  width: 260px;
}

.selection-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
  font-weight: 500;
  color: var(--palette-color-default-lighten-30);
}

.card.selected {
  border: 2px solid var(--palette-color-brand-primary);
}

.card.selected .selection-container {
  color: var(--palette-color-brand-primary);
}

.icon-overlay {
  position: relative;
  display: flex;
  align-items: center;
}

.stat-cont {
  width: 50%;
}

.profile-button {
  margin: 1rem 0;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;

  &:hover {
    svg {
      path {
        fill: var(--palette-color-base-white);
      }
    }
  }
}

header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

header .logo {
  height: 44px;
  width: 44px;
  border-radius: 50%;
  object-fit: cover;
  position: relative;
  display: inline-block;
  border: 1px solid var(--palette-color-default-lighten-90);
  background-color: var(--palette-color-default-lighten-95);
}

header .img-placeholder {
  height: 44px;
  width: 44px;
  border-radius: 50%;
  background-color: var(--palette-color-default-lighten-90);
  display: flex;
  justify-content: center;
  align-items: center;
}

header .img-placeholder .icon {
  color: var(--palette-color-default-lighten-60);
  font-size: 22px;
}

.stats {
  display: flex;
}

.divider {
  width: 0;
  margin: 5px 10px;
  border: 1px solid var(--palette-color-default-lighten-90);
}

.stat {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: var(--palette-color-default);
}

.explainer {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  color: var(--palette-color-default-lighten-20);
}

.radio-box {
  border-radius: 50%;
  width: 28px;
  height: 28px;
  border: 1px solid var(--palette-color-default-lighten-60);
  display: flex;
  justify-content: center;
  align-items: center;
}

.radio-box .icon {
  display: none;
  color: var(--palette-color-base-white);
}

.card.selected .radio-box .icon {
  display: block;
  color: var(--palette-color-base-white);
}

.card.selected .radio-box {
  border: 1px solid var(--palette-color-brand-primary);
  background-color: var(--palette-color-brand-primary);
}

.name {
  font-size: 18px;
  font-weight: 500;
}

.rating .icon {
  color: var(--palette-color-warning);
}

.location {
  color: var(--palette-color-default-lighten-20);
  margin-bottom: 1rem;
  margin-top: 0.25rem;

  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.01em;
}

.past-roles {
  padding-top: 8px;
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
}
</style>
